import * as z from 'zod';
import {
  firstNameSchema,
  lastNameSchema,
  emailSchema,
  phoneNumberSchema,
  zipCodeSchema,
  addressSchema,
  citySchema,
  stateSchema,
  dateOfBirthSchema,
} from '@/lib/schemas';
import { SIGNUP_EMAIL_OTP_ENABLED } from './constant';

// Middle name validation schema for cleaner registration
// Using custom validation since cleaner registration has different logic
export const middleNameValidationShape = z
  .object({
    noMiddlename: z.boolean(),
    middleName: z.string().optional(),
  })
  .refine(
    data => {
      // If noMiddlename is false, middleName is required
      if (!data.noMiddlename) {
        return data.middleName && data.middleName.trim().length > 0;
      }
      return true;
    },
    {
      message: 'Middle name is required',
      path: ['middleName'],
    }
  );

// OTP validation schema
const otpCodeSchema = z
  .string()
  .min(6, 'OTP code must be exactly 6 digits')
  .max(6, 'OTP code must be exactly 6 digits')
  .regex(/^\d+$/, 'OTP code must contain only numbers');

// Email verification schema for signup
export const emailVerificationSchema = z.object({
  email: emailSchema,
  emailOtpCode: otpCodeSchema.optional(),
  isEmailVerified: z.boolean(),
});

// Step-specific schemas for validation
export const personalInfoSchema = z.object({
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  email: emailSchema,
  phoneNumber: phoneNumberSchema,
  zipCode: zipCodeSchema,
  address: addressSchema,
  city: citySchema,
  state: stateSchema,
  middleNameInfo: middleNameValidationShape,
  dateOfBirth: dateOfBirthSchema,
});

// Signup step schema with email verification
export const signupStepWithOtpSchema = z
  .object({
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    email: emailSchema,
    phoneNumber: phoneNumberSchema,
    zipCode: zipCodeSchema,
    address: addressSchema,
    city: citySchema,
    state: stateSchema,
    middleNameInfo: middleNameValidationShape,
    dateOfBirth: dateOfBirthSchema,
    emailOtpCode: otpCodeSchema.optional(),
    isEmailVerified: z.boolean(),
  })
  .refine(data => !SIGNUP_EMAIL_OTP_ENABLED || data.isEmailVerified === true, {
    message: 'Email verification is required',
    path: ['isEmailVerified'],
  });
export const cleanerProfileSchema = z.object({
  profilePic: z
    .union([z.string(), z.instanceof(File), z.undefined()])
    .refine(value => value !== undefined && value !== null && value !== '', {
      message: 'Profile picture is required',
    }),
  bio: z.string().min(10, { message: 'Bio must be at least 10 characters' }),
  languages: z
    .array(z.string())
    .min(1, { message: 'Select at least one language' }),
  workPreferences: z
    .array(z.string())
    .min(1, { message: 'Select at least one work preference' }),
  // availableDays: z
  //   .array(
  //     z.object({
  //       day: z.string(),
  //       start_time: z.string(),
  //       end_time: z.string(),
  //       is_available: z.boolean(),
  //     })
  //   )
  //   .refine(days => days.some(day => day.is_available), {
  //     message: 'Please select at least one available day',
  //   }),
  distanceToTravel: z.number().min(0).max(100),
  positionAddress: z.object({
    lat: z.number(),
    lng: z.number(),
  }),
});

export const equipmentImagesSchema = z.object({
  basicCleaningSuppliesImage: z.any().optional(),
  toolsAndEquipmentImage: z.any().optional(),
  disposableItemsImage: z.any().optional(),
  optionalSpecialtyItemsImage: z.any().optional(),
});

export const payoutSchema = z.object({
  setupIntent: z.any().optional(),
});

export const securityCheckSchema = z.object({
  ssn: z
    .string()
    .regex(
      /^\d{3}-?\d{2}-?\d{4}$/,
      'Please enter a valid SSN (XXX-XX-XXXX or XXXXXXXXX)'
    )
    .refine(val => {
      // Remove dashes and check for invalid SSNs
      const cleaned = val.replace(/-/g, '');
      // Check for common invalid patterns
      const invalidPatterns = [
        /^000/, // Cannot start with 000
        /^666/, // Cannot start with 666
        /^9/, // Cannot start with 9
        /^(\d)\1{8}$/, // Cannot be all the same digit
      ];
      return !invalidPatterns.some(pattern => pattern.test(cleaned));
    }, 'Please enter a valid SSN'),
  sendBackgroundCheckCopy: z.boolean(),
});

export const backgroundCheckSchema = z.object({
  backgroundCheckAccepted: z.boolean().refine(val => val === true, {
    message: 'You must accept the background check policy to continue',
  }),
});

// Legacy schemas for backward compatibility
export const signupSchema = personalInfoSchema;
export const cleanerInfoSchema = z.object({
  ...cleanerProfileSchema.shape,
  ...equipmentImagesSchema.shape,
});

// Combined schema for all registration steps
export const registrationSchema = z.object({
  // Personal info fields
  ...personalInfoSchema.shape,
  // Cleaner profile fields
  ...cleanerProfileSchema.shape,
  // NOTE: Phase 2 fields disabled - Equipment images fields
  // ...equipmentImagesSchema.shape,
  // NOTE: Phase 2 fields disabled - Payout fields
  // ...payoutSchema.shape,
  // Security check fields
  ...securityCheckSchema.shape,
  // Background check fields
  ...backgroundCheckSchema.shape,
});

// Step validation mapping
export const stepFields: Record<number, (keyof RegistrationFormValues)[]> = {
  1: [
    'firstName',
    'lastName',
    'email',
    'phoneNumber',
    'zipCode',
    'address',
    'city',
    'state',
    'middleNameInfo',
    'dateOfBirth',
  ],
  2: [
    'profilePic',
    'bio',
    'languages',
    'workPreferences',
    // NOTE: Phase 2 fields disabled - available days field
    // 'availableDays',
    'distanceToTravel',
    'positionAddress',
  ],
  // NOTE: Phase 2 fields disabled - Equipment images step
  // 3: [
  //   'basicCleaningSuppliesImage',
  //   'toolsAndEquipmentImage',
  //   'disposableItemsImage',
  //   'optionalSpecialtyItemsImage',
  // ],
  3: ['ssn'], // Security check step
  4: ['sendBackgroundCheckCopy', 'backgroundCheckAccepted'], // Background check moved to step 4
};

// Type exports
export type SignupFormValues = z.infer<typeof signupSchema>;
export type CleanerInfoFormValues = z.infer<typeof cleanerInfoSchema>;
export type RegistrationFormValues = z.infer<typeof registrationSchema>;
export type PersonalInfoFormValues = z.infer<typeof personalInfoSchema>;
export type CleanerProfileFormValues = z.infer<typeof cleanerProfileSchema>;
export type EquipmentImagesFormValues = z.infer<typeof equipmentImagesSchema>;
export type PayoutFormValues = z.infer<typeof payoutSchema>;
export type SecurityCheckFormValues = z.infer<typeof securityCheckSchema>;
export type BackgroundCheckFormValues = z.infer<typeof backgroundCheckSchema>;
export type EmailVerificationFormValues = z.infer<
  typeof emailVerificationSchema
>;
export type SignupStepWithOtpFormValues = z.infer<
  typeof signupStepWithOtpSchema
>;
